import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Safely parse a date value that could be a string, Date object, or other type
 * Returns null if the value cannot be parsed as a valid date
 */
export function safeParseUtcDate(dateValue: unknown): Date | null {
  if (!dateValue) return null;

  let dateString: string;
  if (typeof dateValue === 'string') {
    dateString = dateValue.trim();
  } else if (dateValue instanceof Date) {
    return dateValue;
  } else {
    return null;
  }

  if (dateString === '') return null;

  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Safely parse a date-only string (yyyy-MM-dd) as local midnight.
 * Unlike `new Date('yyyy-MM-dd')` which parses as UTC midnight, this returns
 * a Date at midnight in the local timezone, preventing off-by-one day errors
 * when comparing with `new Date()` across DST boundaries or timezone offsets.
 */
export function safeParseLocalDate(dateValue: unknown): Date | null {
  if (!dateValue) return null;

  let dateString: string;
  if (typeof dateValue === 'string') {
    dateString = dateValue.trim();
  } else if (dateValue instanceof Date) {
    return dateValue;
  } else {
    return null;
  }

  if (dateString === '') return null;

  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const date = new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10));
    return isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}